import React, {useState, useEffect} from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import {
  updateLead,
} from '../../../services/api/lead/lead';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DEAL_STAGE, Lead, DealStage } from '@tassomutuo-backoffice/types';
import { CtaButton } from '@tassomutuo-backoffice/ui';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import useLeadActionRequired from '../hooks/useLeadActionRequired';
import LabelValueItem from './LabelValueItem';
import useDateFormat from '../../../hooks/useDateFormat';
import formatMoney from '../../../utils/formatMoney';
import { needUpdateNotify } from '../../../services/api/lead/lead';
import { getUserRoleInLocalStorage } from '../../../services/localStorage/localStorage';
import { logMixPanelEvent } from '../../../services/tracking/mixPanel';
import ModalLeadDropForm from './ModalLeadDropForm';

interface IProps {
  lead: Lead;
}

const LeadCardList = ({ lead }: IProps) => {
  const { renderActionRequired } = useLeadActionRequired();
  const { formatDate } = useDateFormat();

  const queryClient = useQueryClient();

  const renderAction = renderActionRequired({ lead });

  const [isAdmin, setIsAdmin] = useState(false);
  const [showModalDropForm, setShowModalDropForm] = useState(false);

  useEffect(() => {
    if (getUserRoleInLocalStorage() === 'admin') {
      setIsAdmin(true);
    }
  }, []);

  const id = lead.properties.hs_object_id;

  const lost = lead.properties.mutuo_perso === 'true';

  const updateLeadMutation = useMutation({
    mutationFn: updateLead,
    onSuccess: (data, variables, context) => {
      logMixPanelEvent('lead update', { lead_id: id });
      queryClient.invalidateQueries({
        queryKey: ['leadDetails', id],
      });
      queryClient.invalidateQueries({
        queryKey: ['leadsUnderManagement'],
      });

      queryClient.invalidateQueries({
        queryKey: ['leadNotes', id],
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const updateLeadHandler = async ({
    dealStage,
    loanAmount,
    propertyValue,
    note,
    droppedMotivation,
    attoDate,
    commission,
    appointmentDate,
    appointmentTime,
    appointmentLocation,
    meeting_done
  }: {
    dealStage: DealStage;
    loanAmount: string;
    propertyValue: string;
    note: string;
    droppedMotivation: string;
    attoDate: Date | null;
    commission: string;
    appointmentDate: Date | null;
    appointmentTime: string | null;
    appointmentLocation: string;
    meeting_done?: boolean;
  }) => {
    await updateLeadMutation.mutateAsync({
      actionType: 'updateStatus',
      leadId: id!,
      dealName: lead?.properties.dealname || 'Sconosciuta',
      dealStage,
      currentDealStage: lead?.properties.dealstage || undefined,
      loanAmount,
      propertyValue,
      note,
      droppedMotivation,
      attoDateTimestamp: attoDate
        ? Date.UTC(
            +formatDate(attoDate, 'yyyy'),
            attoDate.getMonth(),
            +formatDate(attoDate, 'dd')
          )
        : undefined,
      commission,
      appointmentTimestamp: appointmentDate
        ? appointmentDate.getTime()
        : undefined,
      appointmentTime: appointmentTime || undefined,
      appointmentLocation,
      salesManagerEmail: lead?.properties?.sales_manager_user?.email,
      meeting_done: meeting_done+'' || lead?.properties?.meeting_done
    });

    if (dealStage === DEAL_STAGE.DROPPED) {
      queryClient.invalidateQueries({
        queryKey: ['leadsDropped'],
      });
    }
    if (dealStage === DEAL_STAGE.ATTO_NOTARILE_CONCLUSO) {
      queryClient.invalidateQueries({
        queryKey: ['leadsClosed'],
      });
    }

    const successMessage =
      dealStage === DEAL_STAGE.DROPPED
        ? 'La tua richiesta di cambio stato a DROPPED è stata inviata'
        : 'Lead modificata con successo';
    toast.success(successMessage);
  };

  const addNoteHandler = async ({
    dealStage,
    note,
  }: {
    dealStage: DealStage | undefined;
    note: string;
  }) => {
    await updateLeadMutation.mutateAsync({
      actionType: 'updateStatus',
      leadId: id!,
      dealName: lead?.properties.dealname || 'Sconosciuta',
      dealStage,
      currentDealStage: lead?.properties.dealstage || undefined,
      note
    });

    if (dealStage === DEAL_STAGE.DROPPED) {
      queryClient.invalidateQueries({
        queryKey: ['leadsDropped'],
      });
    }
    if (dealStage === DEAL_STAGE.ATTO_NOTARILE_CONCLUSO) {
      queryClient.invalidateQueries({
        queryKey: ['leadsClosed'],
      });
    }

    queryClient.invalidateQueries({
      queryKey: ['leadsDropped'],
    });

    queryClient.invalidateQueries({
      queryKey: ['leadsUnderManagement'],
    });

    const successMessage =
      dealStage === DEAL_STAGE.DROPPED
        ? 'La tua richiesta di cambio stato a DROPPED è stata inviata'
        : 'Lead modificata con successo';
    toast.success(successMessage);
  };

  const leadProperties = lead.properties;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'white',
        m: 4,
        p: 2,
        border: lead.properties.dealstage === DEAL_STAGE.CONTATTARE_CLIENTE ? `1px solid rgba(239, 68, 68, 1)` : 'inherit',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap-reverse',
            gap: 2,
            borderBottom: (th) => `1px solid ${th.palette.divider}`,
            width: '100%',
            pb: 1,
          }}
        >
          <Typography
            fontWeight="bold"
            textTransform="uppercase"
            sx={{
              color: lost
                ? 'rgba(239, 68, 68, 1)'
                : (th) => th.palette.secondary.dark,
            }}
          >
            {leadProperties.readable_dealstage.label}
            {lost ? ' - MUTUO PERSO ' : ''}
            {lost && lead.properties.data_operazione_immobiliare
              ? formatDate(
                  lead.properties.data_operazione_immobiliare,
                  'dd/MM/yyyy'
                )
              : ''}
            {lost ? ' - ' + formatMoney(Number(lead.properties.amount)) : ''}
          </Typography>
          {renderAction}
          {isAdmin ? (
            <CtaButton
              sx={{
                bgcolor: (th) => th.palette.primary.dark,
                width: { sm: '100%', xs: '100%' },
                maxWidth: '140px',
              }}
              variant="contained"
              onClick={() => needUpdateNotify(lead)}
            >
              <MailOutlineIcon />
              <Typography fontWeight="bold" pl={1}>
                Notifica
              </Typography>
            </CtaButton>
          ) : null}
        </Box>

        <Box pt={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography
                sx={{ color: (th) => th.palette.secondary.dark }}
                variant="h5"
                fontWeight="bold"
              >
                {leadProperties.dealname}
              </Typography>
              <Typography variant="h6">
                {leadProperties.phone_number}
              </Typography>
            </Box>
            <LabelValueItem
              containerSx={{ alignItems: { sm: 'flex-end' } }}
              label="Ultima modifica"
              value={
                leadProperties.notes_last_updated
                  ? formatDate(
                      leadProperties.notes_last_updated,
                      'dd/MM/yyyy HH:mm'
                    )
                  : 'N/A'
              }
            />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            flexWrap="wrap-reverse"
            gap={2}
            mt={2}
          >
            <Box display="flex" gap={4}>
              <LabelValueItem
                label="Valore immobile"
                value={
                  leadProperties.property_value
                    ? formatMoney(+leadProperties.property_value)
                    : 'N/A'
                }
              />

              <LabelValueItem
                label="Importo mutuo"
                value={
                  leadProperties.loan_amount
                    ? formatMoney(+leadProperties.loan_amount)
                    : 'N/A'
                }
              />
            </Box>

            <LabelValueItem
              containerSx={{ alignItems: { sm: 'flex-end' } }}
              label="Data creazione"
              value={
                leadProperties.createdate
                  ? formatDate(leadProperties.createdate, 'dd/MM/yyyy HH:mm')
                  : 'N/A'
              }
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: 2,
          bgcolor: 'white',
          mt: 2,
        }}
      >
        {(leadProperties.dealstage === DEAL_STAGE.IN_SVILUPPO ||
          leadProperties.dealstage === DEAL_STAGE.INCARICO_O_VOUCHER_OTTENUTO ||
          leadProperties.dealstage === DEAL_STAGE.IN_ATTESA_DI_DELIBERA ||
          leadProperties.dealstage === DEAL_STAGE.DELIBERATA) && (
          <Box
            sx={{
              width: { sm: '100%', xs: '100%' },
              maxWidth: '400px',
              alignSelf: 'flex-end',
            }}
          >
            <CtaButton
              sx={{
                // bgcolor: (th) => th.palette.success.main,
                width: { sm: '100%', xs: '100%' },
                maxWidth: '400px',
              }}
              color="success"
              variant="outlined"
              onClick={() =>
                addNoteHandler({
                  dealStage: leadProperties.dealstage,
                  note: "Il cliente è ancora in cerca dell'immobile",
                })
              }
            >
              <Typography variant="h6" fontWeight="bold" p={1}>
                Sta cercando immobile
              </Typography>
            </CtaButton>
          </Box>
        )}
        {(leadProperties.dealstage === DEAL_STAGE.CONTATTARE_CLIENTE ||
          leadProperties.dealstage === DEAL_STAGE.APPUNTAMENTO_DA_CONFERMARE ||
          leadProperties.dealstage === DEAL_STAGE.APPUNTAMENTO_CONFERMATO ||
          leadProperties.dealstage ===
            DEAL_STAGE.APPUNTAMENTO_ANNULLATO_STB_15GG ||
          leadProperties.dealstage === DEAL_STAGE.DROPPED ||
          leadProperties.dealstage ===
            DEAL_STAGE.APPUNTAMENTO_DA_RIFISSARE) && (
          <Box
            sx={{
              width: { sm: '100%', xs: '100%' },
              maxWidth: '400px',
              alignSelf: 'flex-end',
            }}
          >
            <CtaButton
              sx={{
                // bgcolor: (th) => th.palette.success.main,
                width: { sm: '100%', xs: '100%' },
                maxWidth: '400px',
              }}
              color="success"
              variant="outlined"
              onClick={() =>
                addNoteHandler({
                  dealStage: DEAL_STAGE.IN_SVILUPPO,
                  note: 'Cliente incontrato e fattibile',
                })
              }
            >
              <Typography variant="h6" fontWeight="bold" p={1}>
                Cliente incontrato e fattibile
              </Typography>
            </CtaButton>
          </Box>
        )}
        {leadProperties.dealstage !== DEAL_STAGE.ATTO_NOTARILE_CONCLUSO &&
          leadProperties.dealstage !== DEAL_STAGE.DROPPED && (
            <Box
              sx={{
                width: { sm: '100%', xs: '100%' },
                maxWidth: '400px',
                alignSelf: 'flex-end',
              }}
            >
              <CtaButton
                sx={{
                  // bgcolor: (th) => th.palette.error.main,
                  width: { sm: '100%', xs: '100%' },
                  maxWidth: '400px',
                }}
                color="error"
                variant="outlined"
                onClick={() => setShowModalDropForm(true)}
              >
                <Typography variant="h6" fontWeight="bold" p={1}>
                  Droppa la lead
                </Typography>
              </CtaButton>
            </Box>
          )}
      </Box>
      <Box
        sx={{
          width: { sm: '100%', xs: '100%' },
          maxWidth: '400px',
          alignSelf: 'flex-end',
          mt: 2,
        }}
      >
        <Link
          to={`/lead/${leadProperties.hs_object_id}`}
          style={{
            textDecoration: 'none',
          }}
        >
          <CtaButton
            sx={{
              bgcolor: (th) =>
                renderAction ? th.palette.error.main : th.palette.primary.light,
              width: { sm: '100%', xs: '100%' },
              maxWidth: '400px',
            }}
            variant="contained"
          >
            <Typography fontWeight="bold" p={1}>
              Visualizza i dettagli della lead
            </Typography>
          </CtaButton>
        </Link>
      </Box>

      <ModalLeadDropForm
        lead={lead}
        open={showModalDropForm}
        handleClose={() => setShowModalDropForm(false)}
        updateLeadStatus={updateLeadHandler}
      />
    </Box>
  );
};

export default LeadCardList;
